import React from 'react';
import { faKey, faPen, faTrash, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PartnerApiKey } from '@hooks/requests/queries/use_partner_api_keys_query';
import { Badge, RowMenu } from '../ui';
import { LoadingSpinner } from '../feedback';
import { formatDateTime } from '@lib/format_date';

// "API Key" shows `maskedKey` — the client_id (`id`) already
// masked server-side (`PartnerApiKeyResponse.maskedKey` in ally); `id` only
// ever appears here unmasked as an action target (Delete), never rendered.
// "Actions" (sc-45103) opens a Rename/Delete menu.
const COLUMNS = ['Name', 'API Key', 'Created', 'Actions'] as const;

// 2-key cap (AC8/AC9) / 1-key floor to revoke (AC3) — mirrors the backend's
// own gates (`_API_KEY_MAX_COUNT`/`_API_KEY_MIN_COUNT_TO_REVOKE` in
// `web.py`), so the pre-modal toasts fire off the same count already in
// this table's data, without an extra round trip.
export const API_KEY_MAX_COUNT = 2;
const API_KEY_MIN_COUNT_TO_REVOKE = 2;

interface ApiKeysTableProps {
  keys: PartnerApiKey[];
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  onCreateKey?: () => void;
  /**
   * Called when the admin picks Delete on a row and the account currently
   * has 2 keys (AC4's confirmation modal path) — the caller opens it.
   */
  onRevokeKey?: (key: PartnerApiKey) => void;
  /** AC3: revoking the account's only key shows this toast, no modal. */
  onRevokeBlockedByFloor?: () => void;
  /** Called when the admin picks Rename on a row (sc-45257, AC2). */
  onRenameKey?: (key: PartnerApiKey) => void;
}

/**
 * The API Keys page's table (sidebar nav item "API Keys"; Figma "API Keys (30/07)",
 * Actions-column follow-up 3011:10158).
 *
 * Read-only list (sc-45102) plus create/revoke/rename actions: a "New Key"
 * bar button (sc-45103, AC9) and per-row Rename/Delete menu items
 * (sc-45103 AC2-AC3, sc-45257 AC2), reusing `RowMenu` the same way
 * `TeamTable` does. `ApiKeysPage` still decides whether this renders at
 * all (AC1/AC2 gate the whole page).
 *
 * The Sep 2 backend redesign reads keys live from Auth0 instead of a
 * stored record — see `PartnerApiKey`'s doc comment. "API Key" is
 * reproduced, but as `maskedKey` (a masked view of
 * `id`, the client_id) rather than a separate secret value, which Auth0
 * never returns — and that masking now happens on the backend, not here.
 */
const ApiKeysTable: React.FC<ApiKeysTableProps> = ({
  keys,
  isLoading = false,
  error = null,
  onRetry,
  onCreateKey,
  onRevokeKey,
  onRevokeBlockedByFloor,
  onRenameKey,
}) => {
  const handleRevokeClick = (key: PartnerApiKey) => {
    if (keys.length < API_KEY_MIN_COUNT_TO_REVOKE) {
      onRevokeBlockedByFloor?.();
      return;
    }
    onRevokeKey?.(key);
  };

  // ── Error (2992:9634's sibling, "API Keys -failed to load keys" 3084:13788) ──
  // AC7: a failed load gets this dedicated state, not a blank or broken table.
  if (error) {
    return (
      <div className="api-keys-status-block">
        <div className="api-keys-status-icon api-keys-status-icon--danger">
          <FontAwesomeIcon icon={faXmark} />
        </div>
        <h2 className="api-keys-status-title">We couldn&apos;t load your API keys</h2>
        <p className="api-keys-status-description">
          Something went wrong on our side.
          <br />
          Your keys are safe and still active.
        </p>
        {onRetry && (
          <button type="button" className="api-keys-retry-btn" onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="api-keys-status-block">
        <LoadingSpinner size="lg" label="Loading API keys..." />
      </div>
    );
  }

  // ── Empty (Figma "API Keys - empty view" 2992:9634) ──
  // AC6: no keys yet is an empty state, not an error. Now that creating a
  // key is possible from this page (sc-45103), the empty state offers that
  // directly rather than only "reach out to your admin".
  if (keys.length === 0) {
    return (
      <div className="api-keys-status-block">
        <div className="api-keys-status-icon api-keys-status-icon--accent">
          <FontAwesomeIcon icon={faKey} />
        </div>
        <h2 className="api-keys-status-title">No API keys yet</h2>
        <p className="api-keys-status-description">
          API keys let your systems authenticate requests to Ally Connect.
        </p>
        {onCreateKey && (
          <button type="button" className="api-keys-retry-btn" onClick={onCreateKey}>
            <span aria-hidden="true">+</span> New Key
          </button>
        )}
      </div>
    );
  }

  const atKeyLimit = keys.length >= API_KEY_MAX_COUNT;

  return (
    <>
      <div className="api-keys-bar">
        <span className="api-keys-bar-count" data-testid="api-keys-count">
          {`${keys.length} of ${API_KEY_MAX_COUNT} Keys`}
        </span>
        <span className="api-keys-new-key-wrap">
          <button
            type="button"
            className="api-keys-new-key-btn"
            onClick={onCreateKey}
            disabled={!onCreateKey || atKeyLimit}
            aria-describedby={atKeyLimit ? 'api-keys-new-key-tooltip' : undefined}
          >
            <span aria-hidden="true">+</span> New Key
          </button>
          {atKeyLimit && (
            <span id="api-keys-new-key-tooltip" role="tooltip" className="api-keys-new-key-tooltip">
              2-key limit reached. Delete a key to create a new one.
            </span>
          )}
        </span>
      </div>

      <div className="api-keys-table-wrap overflow-x-auto">
        <table className="api-keys-table">
          <thead>
            <tr>
              {COLUMNS.map((label) => (
                <th
                  key={label}
                  className={label === 'Actions' ? 'api-keys-cell-actions' : undefined}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {keys.map((key, index) => (
              // `maskedKey` is the only identifier this row has (the real
              // client_id never reaches the frontend); paired with index in
              // case two keys ever masked to the same visible prefix.
              <tr
                key={`${key.maskedKey}-${index}`}
                className="api-keys-row"
                data-testid="api-keys-row"
              >
                <td>
                  <div className="api-keys-cell-name">
                    <span className="api-keys-cell-name-text">{key.name}</span>
                    {key.isActive && (
                      <Badge variant="success" size="sm">
                        Active
                      </Badge>
                    )}
                  </div>
                </td>
                <td>
                  <span className="api-keys-cell-mono">{key.maskedKey}</span>
                </td>
                <td>
                  <span className="api-keys-cell-date">{formatDateTime(key.createdAt)}</span>
                </td>
                <td className="api-keys-cell-actions">
                  <RowMenu
                    menuClassName="api-keys-menu"
                    triggerLabel={`Actions for ${key.name || key.maskedKey}`}
                    items={[
                      {
                        label: 'Rename',
                        icon: faPen,
                        onClick: () => onRenameKey?.(key),
                      },
                      {
                        label: 'Delete',
                        icon: faTrash,
                        variant: 'danger',
                        onClick: () => handleRevokeClick(key),
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ApiKeysTable;
